import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { slidesApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, Type, Image, Square, Play, ChevronLeft, ChevronRight, Copy, Users, Circle, LayoutDashboard, Download, Move } from 'lucide-react';

const THEMES = {
  default: { bg: '#ffffff', text: '#1a1a1a' },
  dark: { bg: '#1e293b', text: '#f8fafc' },
  blue: { bg: '#1e40af', text: '#ffffff' },
  green: { bg: '#166534', text: '#ffffff' },
  gradient: { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: '#ffffff' },
};

export default function SlideEditor() {
  const { presId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [pres, setPres] = useState(null);
  const [title, setTitle] = useState('');
  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedEl, setSelectedEl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [presSlide, setPresSlide] = useState(0);
  const [activeUsers, setActiveUsers] = useState([]);
  const [dragging, setDragging] = useState(null); // {elId, startX, startY, origX, origY}
  const [resizing, setResizing] = useState(null);
  const canvasRef = useRef(null);
  const saveTimerRef = useRef(null);
  const wsRef = useRef(null);

  // Drag handlers
  useEffect(() => {
    if (!dragging && !resizing) return;
    const onMove = (e) => {
      const scale = 0.65;
      if (dragging) {
        const dx = (e.clientX - dragging.startX) / scale;
        const dy = (e.clientY - dragging.startY) / scale;
        updateElement(dragging.elId, { x: Math.max(0, dragging.origX + dx), y: Math.max(0, dragging.origY + dy) });
      }
      if (resizing) {
        const dx = (e.clientX - resizing.startX) / scale;
        const dy = (e.clientY - resizing.startY) / scale;
        updateElement(resizing.elId, {
          width: Math.max(30, resizing.origW + dx),
          height: Math.max(20, resizing.origH + dy)
        });
      }
    };
    const onUp = () => { setDragging(null); setResizing(null); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging, resizing]);

  // WebSocket connection
  useEffect(() => {
    if (!presId || !token) return;
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsUrl}/api/ws/slide/${presId}?token=${token}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === 'presence') {
        setActiveUsers(msg.active_users || []);
      } else if (msg.type === 'slide_update') {
        setPres(prev => {
          if (!prev) return prev;
          const newSlides = [...(prev.slides || [])];
          if (msg.slide_index >= 0 && msg.slide_index < newSlides.length) {
            newSlides[msg.slide_index] = msg.slide_data;
          }
          return { ...prev, slides: newSlides };
        });
      } else if (msg.type === 'slide_add') {
        setPres(prev => {
          if (!prev) return prev;
          const newSlides = [...(prev.slides || [])];
          newSlides.splice(msg.index ?? newSlides.length, 0, msg.slide_data);
          return { ...prev, slides: newSlides };
        });
      } else if (msg.type === 'slide_delete') {
        setPres(prev => {
          if (!prev) return prev;
          const newSlides = (prev.slides || []).filter((_, i) => i !== msg.slide_index);
          return { ...prev, slides: newSlides };
        });
      }
    };
    return () => { ws.close(); wsRef.current = null; };
  }, [presId, token]);

  useEffect(() => {
    if (!presId) return;
    slidesApi.get(presId).then(p => { setPres(p); setTitle(p.title); }).catch(() => { toast.error('Not found'); navigate('/slides'); });
  }, [presId]);

  const slides = pres?.slides || [];
  const currentSlide = slides[activeSlide] || { elements: [], background: '#ffffff', notes: '' };

  const updateSlides = (newSlides, wsMsg = null) => {
    setPres(prev => ({ ...prev, slides: newSlides }));
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try { await slidesApi.update(presId, { slides: newSlides, title }); } catch {}
    }, 2000);
    if (wsMsg && wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify(wsMsg));
    }
  };

  const addSlide = () => {
    const newSlide = { slide_id: Date.now().toString(), elements: [], background: '#ffffff', notes: '' };
    const newSlides = [...slides, newSlide];
    updateSlides(newSlides, { type: 'slide_add', slide_data: newSlide, index: newSlides.length - 1 });
    setActiveSlide(newSlides.length - 1);
  };

  const deleteSlide = (idx) => {
    if (slides.length <= 1) return;
    const newSlides = slides.filter((_, i) => i !== idx);
    updateSlides(newSlides, { type: 'slide_delete', slide_index: idx });
    setActiveSlide(Math.min(activeSlide, newSlides.length - 1));
  };

  const duplicateSlide = (idx) => {
    const dup = JSON.parse(JSON.stringify(slides[idx]));
    dup.slide_id = Date.now().toString();
    const newSlides = [...slides];
    newSlides.splice(idx + 1, 0, dup);
    updateSlides(newSlides, { type: 'slide_add', slide_data: dup, index: idx + 1 });
    setActiveSlide(idx + 1);
  };

  const addElement = (type) => {
    const el = { id: Date.now().toString(), type, x: 50, y: type === 'text' ? 50 : 150, width: type === 'text' ? 600 : 200, height: type === 'text' ? 60 : 200 };
    if (type === 'text') { el.content = 'Click to edit'; el.fontSize = 24; el.color = currentSlide.background === '#ffffff' ? '#1a1a1a' : '#ffffff'; el.fontWeight = 'normal'; el.textAlign = 'left'; }
    if (type === 'shape') { el.shape = 'rect'; el.fill = '#6366f1'; el.stroke = ''; }
    if (type === 'image') { el.src = ''; }
    const newSlides = [...slides];
    newSlides[activeSlide] = { ...currentSlide, elements: [...currentSlide.elements, el] };
    updateSlides(newSlides, { type: 'slide_update', slide_index: activeSlide, slide_data: newSlides[activeSlide] });
    setSelectedEl(el.id);
  };

  const updateElement = (elId, updates) => {
    const newSlides = [...slides];
    newSlides[activeSlide] = { ...currentSlide, elements: currentSlide.elements.map(e => e.id === elId ? { ...e, ...updates } : e) };
    updateSlides(newSlides, { type: 'slide_update', slide_index: activeSlide, slide_data: newSlides[activeSlide] });
  };

  const deleteElement = (elId) => {
    const newSlides = [...slides];
    newSlides[activeSlide] = { ...currentSlide, elements: currentSlide.elements.filter(e => e.id !== elId) };
    updateSlides(newSlides, { type: 'slide_update', slide_index: activeSlide, slide_data: newSlides[activeSlide] });
    setSelectedEl(null);
  };

  const updateSlideBackground = (bg) => {
    const newSlides = [...slides];
    newSlides[activeSlide] = { ...currentSlide, background: bg };
    updateSlides(newSlides, { type: 'slide_update', slide_index: activeSlide, slide_data: newSlides[activeSlide] });
  };

  const handleSave = async () => {
    setSaving(true);
    try { await slidesApi.update(presId, { slides, title }); toast.success('Saved!'); }
    catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const renderElement = (el, isPresentation = false) => {
    const scale = isPresentation ? 1 : 0.65;
    const style = { position: 'absolute', left: el.x * scale, top: el.y * scale, width: el.width * scale, height: el.height * scale, cursor: isPresentation ? 'default' : 'move' };
    const isSelected = !isPresentation && selectedEl === el.id;

    const onDragStart = (e) => {
      if (isPresentation) return;
      e.stopPropagation();
      setSelectedEl(el.id);
      setDragging({ elId: el.id, startX: e.clientX, startY: e.clientY, origX: el.x, origY: el.y });
    };

    const onResizeStart = (e) => {
      e.stopPropagation();
      e.preventDefault();
      setResizing({ elId: el.id, startX: e.clientX, startY: e.clientY, origW: el.width, origH: el.height });
    };

    const wrapEl = (child) => (
      <div key={el.id} style={style} onMouseDown={onDragStart}
        onClick={(e) => { e.stopPropagation(); !isPresentation && setSelectedEl(el.id); }}
        className={isSelected ? 'ring-2 ring-primary ring-offset-1' : ''}>
        {child}
        {isSelected && (
          <div onMouseDown={onResizeStart} className="absolute -right-1.5 -bottom-1.5 w-3 h-3 bg-primary rounded-full cursor-se-resize border-2 border-white shadow" />
        )}
      </div>
    );

    if (el.type === 'text') {
      return wrapEl(
        isSelected ? (
          <textarea value={el.content} onChange={e => updateElement(el.id, { content: e.target.value })}
            onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}
            style={{ width: '100%', height: '100%', fontSize: el.fontSize * scale, color: el.color, fontWeight: el.fontWeight, textAlign: el.textAlign, background: 'transparent', border: 'none', outline: 'none', resize: 'none', fontFamily: 'inherit', cursor: 'text' }} />
        ) : (
          <div style={{ fontSize: el.fontSize * scale, color: el.color, fontWeight: el.fontWeight, textAlign: el.textAlign, whiteSpace: 'pre-wrap', overflow: 'hidden', width: '100%', height: '100%' }}>{el.content}</div>
        )
      );
    }
    if (el.type === 'shape') {
      return wrapEl(
        <div style={{ width: '100%', height: '100%', backgroundColor: el.fill, borderRadius: el.shape === 'circle' ? '50%' : 4, border: el.stroke ? `2px solid ${el.stroke}` : 'none' }} />
      );
    }
    if (el.type === 'image' && el.src) {
      return wrapEl(<img src={el.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />);
    }
    return null;
  };

  // Presentation mode
  if (presenting) {
    const ps = slides[presSlide] || { elements: [], background: '#ffffff' };
    const bgStyle = ps.background?.startsWith('linear') ? { background: ps.background } : { backgroundColor: ps.background || '#ffffff' };
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" data-testid="presentation-mode"
        onClick={() => { if (presSlide < slides.length - 1) setPresSlide(p => p + 1); else setPresenting(false); }}
        onKeyDown={e => { if (e.key === 'Escape') setPresenting(false); if (e.key === 'ArrowRight') setPresSlide(p => Math.min(slides.length - 1, p + 1)); if (e.key === 'ArrowLeft') setPresSlide(p => Math.max(0, p - 1)); }}
        tabIndex={0}>
        <div className="relative" style={{ width: 960, height: 540, ...bgStyle }}>
          {ps.elements.map(el => renderElement(el, true))}
        </div>
        <div className="absolute bottom-4 right-4 text-white/50 text-sm">{presSlide + 1} / {slides.length} (ESC to exit)</div>
      </div>
    );
  }

  if (!pres) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const selEl = currentSlide.elements.find(e => e.id === selectedEl);
  const bgStyle = currentSlide.background?.startsWith('linear') ? { background: currentSlide.background } : { backgroundColor: currentSlide.background || '#ffffff' };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="slide-editor">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="px-4 py-2 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/slides')} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <Input value={title} onChange={e => setTitle(e.target.value)} onBlur={() => slidesApi.update(presId, { title })}
            className="text-lg font-semibold border-none shadow-none focus-visible:ring-0 px-1 h-auto w-64" data-testid="pres-title" />
          <div className="ml-auto flex items-center gap-2">
            {activeUsers.length > 1 && (
              <div className="flex items-center gap-1 mr-2" data-testid="slide-collab-presence">
                <Users className="w-4 h-4 text-muted-foreground" />
                <div className="flex -space-x-1.5">
                  {activeUsers.slice(0, 5).map((u, i) => (
                    <div key={u.id} className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-background"
                      style={{ backgroundColor: ['#4285F4','#DB4437','#0F9D58','#F4B400','#AB47BC'][i % 5] }}
                      title={u.name}>
                      {u.name?.[0]?.toUpperCase()}
                    </div>
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">{activeUsers.length} online</span>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => addElement('text')} data-testid="add-text"><Type className="w-4 h-4 mr-1" /> Text</Button>
            <Button variant="outline" size="sm" onClick={() => addElement('shape')} data-testid="add-shape"><Square className="w-4 h-4 mr-1" /> Shape</Button>
            <Button variant="outline" size="sm" onClick={() => { const url = prompt('Image URL:'); if (url) { addElement('image'); setTimeout(() => updateElement(Date.now().toString(), { src: url }), 100); } }} data-testid="add-image"><Image className="w-4 h-4 mr-1" /> Image</Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="go-dashboard-btn"><LayoutDashboard className="w-4 h-4 mr-1" /> Home</Button>
            <Button variant="outline" size="sm" onClick={() => { setPresSlide(0); setPresenting(true); }} data-testid="present-btn"><Play className="w-4 h-4 mr-1" /> Present</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-btn"><Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Slide Thumbnails */}
        <div className="w-48 border-r border-border overflow-y-auto p-2 space-y-2 shrink-0">
          {slides.map((s, i) => {
            const thumbBg = s.background?.startsWith('linear') ? { background: s.background } : { backgroundColor: s.background || '#ffffff' };
            return (
              <div key={i} onClick={() => { setActiveSlide(i); setSelectedEl(null); }}
                className={`relative cursor-pointer rounded border transition-all ${i === activeSlide ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/50'}`}
                data-testid={`slide-thumb-${i}`}>
                <div className="aspect-video rounded overflow-hidden relative" style={thumbBg}>
                  {s.elements.map(el => renderElement(el, false))}
                </div>
                <div className="absolute top-1 left-1 text-[9px] bg-black/40 text-white px-1 rounded">{i + 1}</div>
                <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 hover:opacity-100">
                  <button onClick={e => { e.stopPropagation(); duplicateSlide(i); }} className="p-0.5 bg-black/40 rounded"><Copy className="w-2.5 h-2.5 text-white" /></button>
                  {slides.length > 1 && <button onClick={e => { e.stopPropagation(); deleteSlide(i); }} className="p-0.5 bg-black/40 rounded"><Trash2 className="w-2.5 h-2.5 text-red-300" /></button>}
                </div>
              </div>
            );
          })}
          <button onClick={addSlide} className="w-full aspect-video rounded border-2 border-dashed border-border hover:border-primary/50 flex items-center justify-center text-muted-foreground transition-colors" data-testid="add-slide-btn">
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 flex items-center justify-center bg-muted/30 p-4 overflow-auto" onClick={() => setSelectedEl(null)}>
          <div ref={canvasRef} className="relative shadow-lg rounded" style={{ width: 624, height: 351, ...bgStyle }} data-testid="slide-canvas">
            {currentSlide.elements.map(el => renderElement(el))}
          </div>
        </div>

        {/* Properties Panel */}
        <div className="w-56 border-l border-border p-3 overflow-y-auto shrink-0 space-y-4">
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Slide Background</h3>
            <div className="flex gap-1.5 flex-wrap">
              {Object.entries(THEMES).map(([key, t]) => (
                <button key={key} onClick={() => updateSlideBackground(t.bg)}
                  className={`w-7 h-7 rounded border-2 transition-all ${currentSlide.background === t.bg ? 'border-primary scale-110' : 'border-border'}`}
                  style={t.bg.startsWith('linear') ? { background: t.bg } : { backgroundColor: t.bg }} title={key} />
              ))}
            </div>
          </div>
          {selEl && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase">Element Properties</h3>
              {/* Position & Size */}
              <div className="grid grid-cols-2 gap-1.5">
                <div><label className="text-[10px] text-muted-foreground">X</label><Input type="number" value={Math.round(selEl.x)} onChange={e => updateElement(selEl.id, { x: parseInt(e.target.value) || 0 })} className="h-6 text-[10px]" /></div>
                <div><label className="text-[10px] text-muted-foreground">Y</label><Input type="number" value={Math.round(selEl.y)} onChange={e => updateElement(selEl.id, { y: parseInt(e.target.value) || 0 })} className="h-6 text-[10px]" /></div>
                <div><label className="text-[10px] text-muted-foreground">Width</label><Input type="number" value={Math.round(selEl.width)} onChange={e => updateElement(selEl.id, { width: parseInt(e.target.value) || 100 })} className="h-6 text-[10px]" /></div>
                <div><label className="text-[10px] text-muted-foreground">Height</label><Input type="number" value={Math.round(selEl.height)} onChange={e => updateElement(selEl.id, { height: parseInt(e.target.value) || 60 })} className="h-6 text-[10px]" /></div>
              </div>
              {selEl.type === 'text' && (
                <>
                  <div><label className="text-[10px] text-muted-foreground">Font Size</label><Input type="number" value={selEl.fontSize || 24} onChange={e => updateElement(selEl.id, { fontSize: parseInt(e.target.value) || 24 })} className="h-7 text-xs" /></div>
                  <div><label className="text-[10px] text-muted-foreground">Color</label><input type="color" value={selEl.color || '#000000'} onChange={e => updateElement(selEl.id, { color: e.target.value })} className="w-full h-7 rounded cursor-pointer" /></div>
                  <div className="flex gap-1">
                    <button onClick={() => updateElement(selEl.id, { fontWeight: selEl.fontWeight === 'bold' ? 'normal' : 'bold' })} className={`p-1.5 rounded text-xs ${selEl.fontWeight === 'bold' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>B</button>
                  </div>
                </>
              )}
              {selEl.type === 'shape' && (
                <>
                  <div><label className="text-[10px] text-muted-foreground">Fill</label><input type="color" value={selEl.fill || '#6366f1'} onChange={e => updateElement(selEl.id, { fill: e.target.value })} className="w-full h-7 rounded cursor-pointer" /></div>
                  <div className="flex gap-1">
                    <button onClick={() => updateElement(selEl.id, { shape: 'rect' })} className={`px-2 py-1 rounded text-[10px] ${selEl.shape === 'rect' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>Rect</button>
                    <button onClick={() => updateElement(selEl.id, { shape: 'circle' })} className={`px-2 py-1 rounded text-[10px] ${selEl.shape === 'circle' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>Circle</button>
                  </div>
                </>
              )}
              {selEl.type === 'image' && (
                <div><label className="text-[10px] text-muted-foreground">Image URL</label><Input value={selEl.src || ''} onChange={e => updateElement(selEl.id, { src: e.target.value })} className="h-7 text-xs" placeholder="https://..." /></div>
              )}
              <Button variant="destructive" size="sm" className="w-full" onClick={() => deleteElement(selEl.id)}><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
            </div>
          )}
          {/* Speaker Notes */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Speaker Notes</h3>
            <textarea value={currentSlide.notes || ''} onChange={e => {
              const newSlides = [...slides];
              newSlides[activeSlide] = { ...currentSlide, notes: e.target.value };
              updateSlides(newSlides);
            }} placeholder="Add speaker notes..." className="w-full h-20 text-xs bg-muted/30 border border-border rounded-lg p-2 resize-none" data-testid="speaker-notes" />
          </div>
        </div>
      </div>
    </div>
  );
}
